import type { User } from '@gcv/shared';
import type { QueryClient } from '@tanstack/react-query';
import { injectable } from 'inversify';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { action, makeAutoObservable, runInAction } from 'mobx';

import type { UserWithType } from 'domain/types';
import { USER_KEY } from 'tanstack-hooks/keys';
import { DateTimeHelpers } from 'util/dateTime.util';
import { UsersApi } from '../api/UsersApi';
import { getComplianceStore } from './ComplianceStore';
import { getSnackbarStore, SnackbarSeverity } from './SnackBarStore';
import { clearStores } from './store.util';

@injectable()
export class UserStore {
  isLoaded = false;
  isLoading = false;
  isLoggedIn = false;
  mfaInfo: { mfaEnabled: boolean; mfaRequired: boolean } = { mfaEnabled: false, mfaRequired: false };
  user: UserWithType | User = {} as UserWithType | User;
  queryClient: QueryClient = undefined as unknown as QueryClient;

  constructor() {
    makeAutoObservable(this);
  }

  usersApi = new UsersApi();
  complianceStore = getComplianceStore();
  snackbarStore = getSnackbarStore();

  setQueryClient(client: QueryClient) {
    this.queryClient = client;
  }

  load = action(async (id: string, setLoading?: (value: boolean) => void) => {
    try {
      if (setLoading) {
        setLoading(true);
      }
      this.setIsLoading(true);

      const user = await this.usersApi.getUser(id);
      if (user.invitation_status === 'archived') {
        this.setIsLoading(false);
        return user;
      }

      const mfaInfo = await this.usersApi.checkMfaRequired(user.email);

      runInAction(() => {
        this.user = user;
        this.isLoaded = true;
        this.mfaInfo = mfaInfo;
      });

      const updatedUser = await this.usersApi.updateUserIdentification(user.id, {
        lastLogin: DateTimeHelpers.getUtcIsoString(),
        invitation_status: user.invitation_status === 'pending' ? 'accepted' : undefined
      });

      return updatedUser;
    } catch (err) {
      this.snackbarStore.showSnackbar(SnackbarSeverity.Error, 'There was an issue logging in.');
      throw err; //rethrow for rollbar
    } finally {
      this.setIsLoading(false);
    }
  });

  setIsLoading = action(async (isLoading: boolean) => {
    this.isLoading = isLoading;
  });

  // TO DO - deprecate in favor of only accessing and updating user data via the user query
  updateUser = action((updatedUser: User | UserWithType) => {
    // these properties are updated on the identification call on the BE
    // but not needed on the FE
    // and cause a diff for our `userDataIsEqual` check that we don't care about
    const propertiesToIgnore = ['date_updated', 'lastLogin'];
    const omittedUpdatedUser = omit(updatedUser, propertiesToIgnore);
    const omittedLocalUser = omit(this.user, propertiesToIgnore);
    const userDataIsEqual = isEqual(omittedUpdatedUser, omittedLocalUser);

    // do not set store and query cache data if no changes were made
    if (userDataIsEqual) return;

    this.user = updatedUser;
    this.queryClient.setQueryData([USER_KEY, this.user.id], (oldUser?: UserWithType) => {
      const prevUser = oldUser || this.user;
      return { ...prevUser, ...updatedUser };
    });
  });

  updateUserStoreOnly = action((updatedUser: User | UserWithType) => {
    const propertiesToIgnore = ['date_updated', 'lastLogin'];
    const omittedUpdatedUser = omit(updatedUser, propertiesToIgnore);
    const omittedLocalUser = omit(this.user, propertiesToIgnore);
    const userDataIsEqual = isEqual(omittedUpdatedUser, omittedLocalUser);

    if (userDataIsEqual) return;

    this.user = updatedUser;
  });

  clearStore = action(() => {
    this.isLoggedIn = false;
    this.isLoaded = false;
    this.user = {} as User;
    this.mfaInfo = { mfaEnabled: false, mfaRequired: false };
  });

  /**
   * Do not call this directly to logout, instead, use history.replace('/logout') to redirect to our
   * logout component that handles auth0 logout and calls this function
   */
  logout = action(async () => {
    runInAction(() => {
      clearStores();
    });
  });
}

let userStore: UserStore;

export function getUserStore(): UserStore {
  if (!userStore) {
    userStore = new UserStore();
  }

  return userStore;
}
